import { useEffect, useState } from "react";
import { DEMO_MAX_KM, formatKm } from "./journeyModel";
import { AGGREGATE_REFRESH_INTERVAL_MS } from "./progressData";

function formatLastUpdated(value) {
  if (!value) return "Not available";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";

  return date.toLocaleString("en-CA", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function formatNextUpdate(value, nowMs) {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const nextUpdateMs = date.getTime() + AGGREGATE_REFRESH_INTERVAL_MS;
  const minutesRemaining = Math.max(0, Math.ceil((nextUpdateMs - nowMs) / 60000));

  if (minutesRemaining <= 0) {
    return "next update soon";
  }

  if (minutesRemaining === 1) {
    return "next update in 1 minute";
  }

  return `next update in ${minutesRemaining} minutes`;
}

export default function ProgressPanel({
  distanceKm,
  journeyPercent,
  onChangeDistance,
  onChangeViewZoom,
  onCtaClick,
  onPreventControlFocusScroll,
  onTogglePanel,
  panelCollapsed,
  progressData,
  progressDebugEnabled,
  stats,
  viewZoom,
  viewZoomMax,
  viewZoomMin,
  viewZoomStep,
}) {
  const [nowMs, setNowMs] = useState(Date.now());
  const nextUpdateLabel = formatNextUpdate(progressData.lastUpdatedAt, nowMs);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, []);

  return (
    <div className={`bcr-demo-panel ${panelCollapsed ? "bcr-demo-panel-collapsed" : ""}`}>
      <div className="bcr-panel-header">
        <div>
          <p className="bcr-eyebrow">The big Canada Run 2026</p>
          <h1>Earth To Moon Global Challenge</h1>
        </div>
        <div className="bcr-panel-summary">
          <span>{formatKm(distanceKm)} km</span>
          <button
            type="button"
            className={panelCollapsed ? "bcr-panel-view-more" : ""}
            aria-expanded={!panelCollapsed}
            aria-controls="bcr-demo-panel-body"
            aria-label={panelCollapsed ? "Expand progress controls" : "Collapse progress controls"}
            onClick={onTogglePanel}
          >
            {panelCollapsed ? "View More" : "-"}
          </button>
        </div>
      </div>
      <div id="bcr-demo-panel-body" className="bcr-panel-body" hidden={panelCollapsed}>
        <div className="bcr-stat-grid">
          <div>
            <span>Total km</span>
            <strong>{formatKm(distanceKm)}</strong>
          </div>
          <div>
            <span>Lap</span>
            <strong>#{stats.currentLap}</strong>
          </div>
          <div>
            <span>Phase</span>
            <strong>{stats.phase}</strong>
          </div>
          <div>
            <span>From Earth</span>
            <strong>{formatKm(stats.fromEarth)} km</strong>
          </div>
          <div>
            <span>To Moon</span>
            <strong>{formatKm(stats.toMoon)} km</strong>
          </div>
          <a
            className="bcr-stat-card-link"
            href="https://thebigcanadarun.ca/"
            target="_blank"
            rel="noreferrer"
            onClick={onCtaClick}
          >
            <span>Learn More</span>
            <strong>About The Big Canada Run</strong>
          </a>
        </div>
        <p className="bcr-panel-status">
          {progressData.loading
            ? "Loading public aggregate..."
            : progressData.error ||
              `Updated ${formatLastUpdated(progressData.lastUpdatedAt)}${
                nextUpdateLabel ? ` (${nextUpdateLabel})` : ""
              }`}
        </p>
        {progressDebugEnabled && (
          <>
            <label className="bcr-slider-label" htmlFor="bcr-distance-slider">
              Demo aggregate kilometers
            </label>
            <input
              id="bcr-distance-slider"
              className="bcr-distance-slider"
              type="range"
              min="0"
              max={DEMO_MAX_KM}
              step="500"
              value={Math.round(distanceKm)}
              onChange={(event) => onChangeDistance(Number(event.target.value))}
            />
          </>
        )}
        <div className="bcr-progress-bar">
          <span style={{ width: `${journeyPercent}%` }} />
        </div>
        <div className="bcr-zoom-control">
          <label className="bcr-slider-label" htmlFor="bcr-view-zoom-slider">
            View zoom
          </label>
          <div className="bcr-zoom-row">
            <button
              type="button"
              aria-label="Zoom out"
              onPointerDown={onPreventControlFocusScroll}
              onMouseDown={onPreventControlFocusScroll}
              onClick={() => onChangeViewZoom(viewZoom - viewZoomStep)}
            >
              -
            </button>
            <input
              id="bcr-view-zoom-slider"
              className="bcr-zoom-slider"
              type="range"
              min={viewZoomMin}
              max={viewZoomMax}
              step={viewZoomStep}
              value={viewZoom}
              onChange={(event) => onChangeViewZoom(Number(event.target.value))}
            />
            <button
              type="button"
              aria-label="Zoom in"
              onPointerDown={onPreventControlFocusScroll}
              onMouseDown={onPreventControlFocusScroll}
              onClick={() => onChangeViewZoom(viewZoom + viewZoomStep)}
            >
              +
            </button>
            <output>{Math.round(viewZoom * 100)}%</output>
          </div>
        </div>
      </div>
    </div>
  );
}
